import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SparringSession, SparringMessage, Goal } from '../types';
import { getSessionById, addMessageToSession, updateMessageInSession } from '../firebase/sparring.service';
import { getGoalById } from '../firebase/goals.service';
import SparringMilestoneCapture from './SparringMilestoneCapture';
import SparringMilestoneGenerator from './SparringMilestoneGenerator';
import SparringCloseDialog from './SparringCloseDialog';
import SparringCompletionCard from './SparringCompletionCard';
import { GoalIcon, SearchIcon, ChatIcon, TipIcon, EditIcon, LaunchIcon, CelebrationIcon, HourglassIcon, SuccessIcon } from '../../../icons';
import { 
  sparringAIService, 
  GoalContext,
  speechRecognitionService,
  calculateCurrentStep,
  calculateCurrentProjectStep,
  STRUCTURED_STEP_NAMES,
  TOTAL_STRUCTURED_STEPS,
  PROJECT_DEFINITION_STEP_NAMES,
  TOTAL_PROJECT_STEPS
} from './ai';

interface SparringChatProps {
  sessionId: string;
  goalId: string;
  ownerId: string;
  currentMilestoneCount: number;
  onMilestoneCreated?: (milestoneId: string) => void;
  onSessionClosed?: () => void;
  onClose?: () => void;
  // Extended context for AI
  entrepreneurContext?: {
    // Self Test Results
    rolePreference?: 'doer' | 'thinker' | 'connector' | 'leader';
    knowledgeLevels?: Record<string, string>;
    
    // Capability Assessment
    capabilityGaps?: string[];
    capabilitySolutions?: Record<string, string>;
    
    // DISC & Werkstijl Profiles
    discProfile?: {
      primary: string;
      secondary: string;
      strengths: string[];
      weaknesses: string[];
    };
    workstyleProfile?: {
      scores?: Record<string, 'low' | 'medium' | 'high'>;
      quickStart?: number;
      factFinder?: number;
      followThru?: number;
      implementor?: number;
      primaryStrength?: string;
    };
    assessmentLevel?: 'none' | 'basic' | 'full';
    
    // Pre-sparring Context
    preSparringAnswers?: Record<string, string>;
    
    // Entrepreneur Balance Patterns
    patterns?: {
      focusDirection?: 'intern' | 'extern' | 'balanced';
      processApproach?: 'plannen' | 'afronding' | 'balanced';
    };
    
    // Language Preference
    languageVariant?: 'praktisch' | 'neutraal' | 'reflectief';
  };
}

/**
 * SparringChat - AI-powered sparring conversation
 * 
 * Features:
 * - Real AI conversation (OpenAI GPT / Gemini)
 * - Speech recognition (voice input)
 * - Text-to-speech (AI speaks back)
 * - Milestone capture from conversation
 */
const SparringChat: React.FC<SparringChatProps> = ({
  sessionId,
  goalId,
  ownerId,
  currentMilestoneCount,
  onMilestoneCreated,
  onSessionClosed,
  onClose,
  entrepreneurContext,
}) => {
  // Session & messages state
  const [session, setSession] = useState<SparringSession | null>(null);
  const [goal, setGoal] = useState<Goal | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Speech state
  const [isVoiceMode, setIsVoiceMode] = useState(false); // Voice sparring mode
  const [isListening, setIsListening] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false); // Mic mute - pause listening without leaving voice mode
  const [interimTranscript, setInterimTranscript] = useState('');
  const isVoiceModeRef = useRef(false); // Ref to track voice mode in callbacks
  const isMicMutedRef = useRef(false); // Ref to track mute state in callbacks

  // Overlay State Machine - Single source of truth for overlay flow
  type OverlayState = 
    | 'NONE'                    // No overlay
    | 'SPARRING_COMPLETE'       // Step 1: Sparring Voltooid
    | 'GENERATE_MILESTONES'     // Step 2: Mijlpalen Genereren
    | 'SAVING_MILESTONES'       // Step 3: Saving (loading state)
    | 'COMPLETION_CARD'         // Step 4: Gefeliciteerd
    | 'CLOSE_DIALOG';           // Step 5: Sessie Afsluiten
  
  const [overlayState, setOverlayState] = useState<OverlayState>('NONE');
  const [finalMilestoneCount, setFinalMilestoneCount] = useState<number>(0);
  
  // Track if milestones have been generated to prevent auto-resetting to SPARRING_COMPLETE
  const milestonesGeneratedRef = useRef(false);
  
  // Legacy support for manual milestone capture
  const [showMilestoneCapture, setShowMilestoneCapture] = useState(false);

  // Geen max limiet meer - altijd meer mijlpalen mogelijk
  const nextMilestoneNumber = currentMilestoneCount + 1;
  const canAddMilestones = true;
  const milestoneButtonLabel = currentMilestoneCount === 0 
    ? 'Genereer Mijlpalen' 
    : `Mijlpaal ${nextMilestoneNumber} vastleggen`;
  const milestonePrimaryButtonClass = 'mt-3 inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium';
  const milestoneInlineButtonClass = 'flex items-center gap-2 px-3 py-1.5 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors';
  const milestoneSecondaryButtonClass = milestonePrimaryButtonClass.replace('mt-3 inline-flex ', 'flex ');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const initialResponseSentRef = useRef<string | null>(null); // Track if initial AI response was sent for this session
  const isGeneratingRef = useRef(false); // Guard against concurrent AI generation

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const [sessionData, goalData] = await Promise.all([
          getSessionById(sessionId),
          getGoalById(goalId)
        ]);
        
        setSession(sessionData);
        setGoal(goalData);

        // If this is a new session (no messages), get AI to start the conversation
        // Use ref to prevent duplicate calls (React StrictMode calls effects twice)
        if (sessionData && sessionData.messages.length === 0 && goalData) {
          if (initialResponseSentRef.current !== sessionId) {
            initialResponseSentRef.current = sessionId;
            await generateAIResponse(sessionData, goalData, []);
          }
        }
      } catch (err) {
        console.error('Failed to load session:', err);
        setError('Kon sessie niet laden');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [sessionId, goalId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session?.messages, isAIThinking]);

  // State Machine: Auto-transition to SPARRING_COMPLETE when session is done
  // Track if we've already shown the completion overlay to prevent re-showing
  const hasShownCompletionRef = useRef(false);
  
  useEffect(() => {
    if (!session) return;
    if (hasShownCompletionRef.current) return; // Don't re-show if already shown
    
    const currentStep = session.mode === 'structured' 
      ? calculateCurrentStep(session.messages) 
      : session.mode === 'project'
      ? calculateCurrentProjectStep(session.messages)
      : 0;
    const isComplete = (session.mode === 'structured' && currentStep >= TOTAL_STRUCTURED_STEPS) ||
                       (session.mode === 'project' && currentStep >= TOTAL_PROJECT_STEPS);
    
    // Only auto-transition to SPARRING_COMPLETE if:
    // 1. Session is complete
    // 2. We're at NONE state
    // 3. AI is not thinking
    // 4. We haven't shown it before
    if (isComplete && overlayState === 'NONE' && !isAIThinking) {
      console.log('🎯 State Machine: Session complete (first time), transitioning to SPARRING_COMPLETE');
      hasShownCompletionRef.current = true;
      setOverlayState('SPARRING_COMPLETE');
    }
  }, [session, overlayState, isAIThinking]);

  // Debug: Log overlay state machine
  useEffect(() => {
    console.log('🎯 Overlay State Machine:', {
      overlayState,
      finalMilestoneCount,
      session: session ? 'loaded' : 'null'
    });
  }, [overlayState, finalMilestoneCount, session]);

  // ============================================================================
  // AI RESPONSE GENERATION
  // ============================================================================

  const generateAIResponse = useCallback(async (
    currentSession: SparringSession,
    currentGoal: Goal,
    currentMessages: SparringMessage[]
  ) => {
    // Prevent concurrent AI generation calls
    if (isGeneratingRef.current) {
      console.log('AI generation already in progress, skipping duplicate call');
      return;
    }

    if (!sparringAIService.isAvailable()) {
      setError('AI is niet beschikbaar. Controleer je API keys.');
      return;
    }

    isGeneratingRef.current = true;
    setIsAIThinking(true);
    setError(null);

    try {
      // Calculate goal duration in days
      let durationInDays: number | undefined;
      if (currentGoal.startDate && currentGoal.endDate) {
        const start = new Date(currentGoal.startDate);
        const end = new Date(currentGoal.endDate);
        durationInDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      }

      // Build goal context for AI with full entrepreneur context
      const goalContext: GoalContext = {
        title: currentGoal.title,
        description: currentGoal.description,
        goalType: currentGoal.goalType || 'C',
        whyThisGoal: currentGoal.commitment?.name 
          ? `Gecommitteerd door ${currentGoal.commitment.name}` 
          : undefined,
        // Goal timeline
        startDate: currentGoal.startDate,
        endDate: currentGoal.endDate,
        durationInDays,
        // Entrepreneur profile
        rolePreference: entrepreneurContext?.rolePreference,
        knowledgeLevels: entrepreneurContext?.knowledgeLevels,
        // Capability context
        capabilityGaps: entrepreneurContext?.capabilityGaps,
        capabilitySolutions: entrepreneurContext?.capabilitySolutions,
        // Pre-sparring answers
        preSparringContext: entrepreneurContext?.preSparringAnswers,
        // DISC & Werkstijl Assessment
        discProfile: entrepreneurContext?.discProfile,
        workstyleProfile: entrepreneurContext?.workstyleProfile,
        assessmentLevel: entrepreneurContext?.assessmentLevel,
        // ⭐ NEW: Entrepreneur Balance Patterns
        patterns: entrepreneurContext?.patterns,
        // ⭐ NEW: Language Preference (convert Dutch to English)
        languageVariant: entrepreneurContext?.languageVariant 
          ? (entrepreneurContext.languageVariant === 'praktisch' ? 'practical' :
             entrepreneurContext.languageVariant === 'neutraal' ? 'neutral' :
             entrepreneurContext.languageVariant === 'reflectief' ? 'reflective' : undefined)
          : undefined,
      };

      // Generate AI response
      const result = await sparringAIService.generateResponse(
        goalContext,
        currentSession.mode,
        currentMessages
      );

      if (!result.success) {
        throw new Error(result.error || 'AI kon geen antwoord genereren');
      }

      // Save AI message to Firebase
      await addMessageToSession(sessionId, {
        role: 'assistant',
        content: result.content
      });

      // Reload session to get updated messages
      const updatedSession = await getSessionById(sessionId);
      setSession(updatedSession);

    } catch (err: any) {
      console.error('AI response error:', err);
      setError(err.message || 'Fout bij het genereren van AI antwoord');
    } finally {
      isGeneratingRef.current = false;
      setIsAIThinking(false);
    }
  }, [sessionId]);

  // ============================================================================
  // MESSAGE HANDLING
  // ============================================================================

  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || isSending || !session || !goal) return;

    const messageContent = inputValue.trim();
    setInputValue('');
    setInterimTranscript('');
    setIsSending(true);
    setError(null);

    // Optimistic update - add user message locally
    const tempMessage: SparringMessage = {
      id: `temp_${Date.now()}`,
      role: 'user',
      content: messageContent,
      timestamp: Date.now(),
    };

    const updatedMessages = [...session.messages, tempMessage];
    setSession(prev => prev ? {
      ...prev,
      messages: updatedMessages,
    } : null);

    try {
      // Save user message to Firebase
      await addMessageToSession(sessionId, {
        role: 'user',
        content: messageContent,
      });

      // Reload session to get real message ID
      const updated = await getSessionById(sessionId);
      setSession(updated);
      setIsSending(false);

      // Generate AI response
      if (updated) {
        await generateAIResponse(updated, goal, updated.messages);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Kon bericht niet versturen');
      // Rollback on error
      setSession(prev => prev ? {
        ...prev,
        messages: prev.messages.filter(m => m.id !== tempMessage.id),
      } : null);
      setInputValue(messageContent);
      setIsSending(false);
    }
  }, [inputValue, isSending, session, goal, sessionId, generateAIResponse]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ============================================================================
  // SPEECH RECOGNITION
  // ============================================================================

  // Restart listening helper for voice mode
  const restartListeningIfVoiceMode = useCallback(() => {
    // Don't restart if not in voice mode or if muted
    if (!isVoiceModeRef.current || isMicMutedRef.current) return;
    
    setTimeout(() => {
      // Double-check state hasn't changed
      if (!isVoiceModeRef.current || isMicMutedRef.current) return;
      
      const success = speechRecognitionService.startListening(
        (result) => {
          // Ignore results if muted
          if (isMicMutedRef.current) return;
          
          if (result.isFinal) {
            setInputValue(prev => prev + (prev ? ' ' : '') + result.transcript);
            setInterimTranscript('');
          } else {
            setInterimTranscript(result.transcript);
          }
        },
        (error) => {
          console.log('Speech error in voice mode:', error);
          setIsListening(false);
          // Only restart on no-speech (timeout) - NOT on aborted (that causes loops)
          if (error.includes('no-speech') || error.includes('Geen spraak')) {
            restartListeningIfVoiceMode();
          }
        },
        () => {
          setIsListening(false);
          setInterimTranscript('');
          restartListeningIfVoiceMode();
        },
        { language: 'nl-NL', continuous: true, interimResults: true }
      );
      
      if (success) {
        setIsListening(true);
      }
    }, 300);
  }, []);

  const startListening = useCallback(() => {
    const success = speechRecognitionService.startListening(
      // On result
      (result) => {
        if (result.isFinal) {
          setInputValue(prev => prev + (prev ? ' ' : '') + result.transcript);
          setInterimTranscript('');
        } else {
          setInterimTranscript(result.transcript);
        }
      },
      // On error - only show real errors (aborted is already filtered in service)
      (error) => {
        // Only show persistent errors to user
        if (!error.includes('Geen spraak')) {
          setError(error);
        }
        setIsListening(false);
        // Try to restart if in voice mode and it was a no-speech timeout
        if (error.includes('no-speech') || error.includes('Geen spraak')) {
          restartListeningIfVoiceMode();
        }
      },
      // On end
      () => {
        setIsListening(false);
        setInterimTranscript('');
        // Auto-restart if in voice mode
        restartListeningIfVoiceMode();
      },
      { language: 'nl-NL', continuous: true, interimResults: true }
    );

    if (success) {
      setIsListening(true);
      setError(null);
    }
  }, [restartListeningIfVoiceMode]);

  const stopListening = useCallback(() => {
    speechRecognitionService.stopListening();
    setIsListening(false);
    setInterimTranscript('');
  }, []);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // ============================================================================
  // VOICE MODE
  // ============================================================================

  const toggleVoiceMode = useCallback(() => {
    if (isVoiceMode) {
      // Turning off voice mode
      isVoiceModeRef.current = false;
      isMicMutedRef.current = false;
      if (isListening) {
        stopListening();
      }
      setIsVoiceMode(false);
      setIsMicMuted(false);
    } else {
      // Turning on voice mode - start listening immediately
      isVoiceModeRef.current = true;
      isMicMutedRef.current = false;
      setIsVoiceMode(true);
      setIsMicMuted(false);
      startListening();
    }
  }, [isVoiceMode, isListening, startListening, stopListening]);

  // Toggle mic mute - pause listening without leaving voice mode
  const toggleMicMute = useCallback(() => {
    if (isMicMuted) {
      // Unmuting - resume listening
      isMicMutedRef.current = false;
      setIsMicMuted(false);
      setInterimTranscript('');
      if (isVoiceMode) {
        startListening();
      }
    } else {
      // Muting - pause listening
      isMicMutedRef.current = true;
      setIsMicMuted(true);
      setInterimTranscript('');
      if (isListening) {
        stopListening();
      }
    }
  }, [isMicMuted, isVoiceMode, isListening, startListening, stopListening]);

  // ============================================================================
  // UI HANDLERS
  // ============================================================================

  const handleMilestoneCreated = (milestoneId: string) => {
    // Don't close the form - the component shows its own success state
    // Just notify parent to update the milestone count
    onMilestoneCreated?.(milestoneId);
  };

  const handleSessionClosed = () => {
    // Overlay state is already set to NONE by the Close Dialog handler
    onSessionClosed?.();
  };

  // ============================================================================
  // MESSAGE EDITING
  // ============================================================================

  const handleEditMessage = useCallback(async (messageId: string, newContent: string) => {
    if (!session) return;
    
    try {
      // Update in Firebase
      await updateMessageInSession(sessionId, messageId, newContent);
      
      // Update local state
      setSession(prev => {
        if (!prev) return null;
        return {
          ...prev,
          messages: prev.messages.map(msg => 
            msg.id === messageId 
              ? { ...msg, content: newContent, editedAt: Date.now() }
              : msg
          ),
        };
      });
    } catch (err) {
      console.error('Failed to edit message:', err);
      setError('Kon bericht niet bewerken');
    }
  }, [sessionId, session]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mb-2"></div>
        <p className="text-gray-500">Sparringsessie laden...</p>
      </div>
    );
  }

  if (!session || !goal) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <p className="text-gray-500">Sessie niet gevonden</p>
      </div>
    );
  }

  const aiProvider = sparringAIService.getProvider();
  
  // Calculate current step for structured mode
  const currentStep = session?.mode === 'structured' 
    ? calculateCurrentStep(session?.messages || []) 
    : 0;
  const isSessionComplete = session?.mode === 'structured' && currentStep >= TOTAL_STRUCTURED_STEPS;

  return (
    <div className="bg-white rounded-lg border border-gray-200 flex flex-col h-[600px]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-lg">
            {session.mode === 'structured' ? <GoalIcon size={18} /> : session.mode === 'reflection' ? <SearchIcon size={18} /> : <ChatIcon size={18} />}
          </span>
          <div>
            <h3 className="font-medium text-gray-900">
              {session.mode === 'structured' ? 'Gestructureerde sparring' : session.mode === 'reflection' ? 'Reflectie: Zichtbaarheid' : 'Vrije sparring'}
            </h3>
            <p className="text-xs text-gray-400">
              AI: {aiProvider === 'openai' ? 'GPT-4' : aiProvider === 'gemini' ? 'Gemini' : 'Niet beschikbaar'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Progress bar for structured/project mode */}
      {(session.mode === 'structured' || session.mode === 'project') && (() => {
        const stepNames = session.mode === 'project' ? PROJECT_DEFINITION_STEP_NAMES : STRUCTURED_STEP_NAMES;
        const totalSteps = session.mode === 'project' ? TOTAL_PROJECT_STEPS : TOTAL_STRUCTURED_STEPS;
        const currentStep = session.mode === 'project' 
          ? calculateCurrentProjectStep(session.messages)
          : calculateCurrentStep(session.messages);
        
        return (
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Stap {currentStep} van {totalSteps}: {stepNames[currentStep] || 'Afgerond'}
            </span>
            {isSessionComplete && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                ✓ Sparring voltooid
              </span>
            )}
          </div>
          
          {/* Progress steps - clickable for completed steps */}
          <div className="flex gap-1">
            {Array.from({ length: totalSteps }, (_, i) => {
              const stepNum = i + 1;
              const isCompleted = stepNum < currentStep;
              const isCurrent = stepNum === currentStep;
              const isPending = stepNum > currentStep;
              
              return (
                <div 
                  key={stepNum} 
                  className={`flex-1 flex flex-col items-center ${isCompleted ? 'cursor-pointer group' : ''}`}
                  title={isCompleted ? `Klik Bewerken bij je antwoord om "${stepNames[stepNum]}" te bewerken` : ''}
                >
                  <div 
                    className={`
                      h-2 w-full rounded-full transition-all
                      ${isCompleted ? 'bg-green-500 group-hover:bg-green-400' : ''}
                      ${isCurrent ? 'bg-orange-500' : ''}
                      ${isPending ? 'bg-gray-200' : ''}
                    `}
                  />
                  <span className={`
                    text-xs mt-1 hidden sm:block transition-colors
                    ${isCompleted ? 'text-green-600 group-hover:text-green-500' : ''}
                    ${isCurrent ? 'text-orange-600 font-medium' : ''}
                    ${isPending ? 'text-gray-400' : ''}
                  `}>
                    {stepNames[stepNum]}
                  </span>
                </div>
              );
            })}
          </div>
          
          {/* Edit hint */}
          <p className="text-xs text-gray-400 mt-2 text-center">
            <span className="inline-flex items-center gap-1"><TipIcon size={14} /> Je kunt eerdere antwoorden bewerken via de <EditIcon size={14} className="inline" /> knop</span>
          </p>
        </div>
        );
      })()}

      {/* Error banner */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-always">
        {session.messages.length === 0 && !isAIThinking ? (
          <div className="text-center text-gray-400 py-8">
            <p>De sparringpartner start het gesprek...</p>
          </div>
        ) : (
          <>
            {session.messages.map((message) => (
              <MessageBubble 
                key={message.id} 
                message={message}
                onEdit={message.role === 'user' ? handleEditMessage : undefined}
              />
            ))}
            
            {/* AI thinking indicator */}
            {isAIThinking && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg px-4 py-3 max-w-[80%]">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                    <span className="text-sm text-gray-500">Aan het nadenken...</span>
                  </div>
                </div>
              </div>
            )}

          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* ========================================================================
          OVERLAY STATE MACHINE - Linear flow without loops or skips
          ======================================================================== */}
      
      {/* STEP 1: Sparring Voltooid! - Transition to milestone generation */}
      {overlayState === 'SPARRING_COMPLETE' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideUp {
              from {
                transform: translateY(20px);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
          `}</style>
          <div 
            className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-300 rounded-xl p-6 shadow-2xl max-w-lg w-full"
            style={{ animation: 'slideUp 0.3s ease-out' }}
          >
            <div className="text-center mb-4">
              <span className="mb-2 inline-block"><CelebrationIcon size={48} /></span>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {session?.mode === 'structured' ? 'Sparring Voltooid!' : 'Klaar om Mijlpalen te Maken!'}
              </h3>
              <p className="text-sm text-gray-700">
                {session?.mode === 'structured' 
                  ? `Alle ${TOTAL_STRUCTURED_STEPS} stappen doorlopen`
                  : session?.mode === 'reflection'
                    ? 'Je reflectiegesprek kan nu omgezet worden naar concrete mijlpalen'
                    : 'Je vrije gesprek kan nu omgezet worden naar concrete mijlpalen'
                }
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-4 mb-4 border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <LaunchIcon size={18} />
                <h4 className="font-semibold text-gray-900">Volgende Fase:</h4>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                We gaan nu automatisch je <strong>mijlpalen genereren</strong> op basis van dit gesprek. 
                Deze mijlpalen vormen de basis voor je acties en voortgang.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r">
                <p className="text-xs text-blue-800">
                  <TipIcon size={14} className="inline" /> <strong>Tip:</strong> Je kunt de gegenereerde mijlpalen nog aanpassen voordat je ze opslaat.
                </p>
              </div>
            </div>

            <button
              onClick={() => {
                console.log('🎯 State Machine: SPARRING_COMPLETE → GENERATE_MILESTONES');
                setOverlayState('GENERATE_MILESTONES');
              }}
              className="w-full py-4 px-6 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl text-lg font-bold hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-3"
            >
              <GoalIcon size={24} />
              <span>Naar Volgende Fase: Mijlpalen Genereren →</span>
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 & 3: Mijlpalen Genereren + Saving State */}
      {(overlayState === 'GENERATE_MILESTONES' || overlayState === 'SAVING_MILESTONES') && goal && (
        <SparringMilestoneGenerator
          goal={goal}
          ownerId={ownerId}
          sessionId={sessionId}
          messages={session?.messages || []}
          entrepreneurContext={entrepreneurContext}
          onComplete={(savedCount) => {
            console.log('🎯 State Machine: SAVING_MILESTONES → COMPLETION_CARD, savedCount=', savedCount);
            setFinalMilestoneCount(savedCount);
            milestonesGeneratedRef.current = true; // Mark that milestones have been generated
            setOverlayState('COMPLETION_CARD');
          }}
          onCancel={() => {
            console.log('🎯 State Machine: User cancelled, GENERATE_MILESTONES → SPARRING_COMPLETE');
            setOverlayState('SPARRING_COMPLETE');
          }}
          onMilestoneCreated={handleMilestoneCreated}
        />
      )}

      {/* STEP 4: Gefeliciteerd! - Success card after milestones saved */}
      {overlayState === 'COMPLETION_CARD' && (
        <SparringCompletionCard
          milestoneCount={finalMilestoneCount}
          onContinue={() => {
            console.log('🎯 State Machine: COMPLETION_CARD → Closing to show milestones');
            // Close overlay and return to goal overview to show new milestones
            setOverlayState('NONE');
            onClose?.(); // Close the sparring view and return to goal overview
          }}
        />
      )}

      {/* STEP 5: Sessie Afsluiten - Final confirmation and close */}
      {overlayState === 'CLOSE_DIALOG' && (
        <SparringCloseDialog
          sessionId={sessionId}
          milestoneCount={finalMilestoneCount > 0 ? finalMilestoneCount : currentMilestoneCount}
          onClose={() => {
            console.log('🎯 State Machine: CLOSE_DIALOG → Session closed');
            setOverlayState('NONE');
            handleSessionClosed();
          }}
          onCancel={() => {
            console.log('🎯 State Machine: User cancelled close, CLOSE_DIALOG → COMPLETION_CARD');
            setOverlayState('COMPLETION_CARD');
          }}
        />
      )}

      {/* Milestone Capture - Manual add (legacy, still available) */}
      {showMilestoneCapture && overlayState === 'NONE' && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <SparringMilestoneCapture
            goalId={goalId}
            ownerId={ownerId}
            sessionId={sessionId}
            currentMilestoneCount={currentMilestoneCount}
            messages={session?.messages || []}
            onMilestoneCreated={handleMilestoneCreated}
            onCancel={() => setShowMilestoneCapture(false)}
            onFinish={() => {
              setShowMilestoneCapture(false);
              setOverlayState('CLOSE_DIALOG');
            }}
          />
        </div>
      )}

      {/* Input */}
      <div className="border-t border-gray-200 p-4">
        {/* Session complete - hide if overlay is active */}
        {isSessionComplete && !showMilestoneCapture && overlayState === 'NONE' && (
          <div className="flex items-center justify-end mb-3">
            <button
              onClick={() => setOverlayState('CLOSE_DIALOG')}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <span>Sessie afsluiten</span>
            </button>
          </div>
        )}

        {/* Actions - normal mode (not complete) */}
        {!isSessionComplete && !showMilestoneCapture && overlayState === 'NONE' && (
          <>
            {/* Hint banner for free/reflection modes after 4+ messages */}
            {session?.mode !== 'structured' && (session?.messages?.length || 0) >= 4 && (
              <div className="mb-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <TipIcon size={18} />
                    <p className="text-sm text-green-800">
                      <span className="font-medium">Klaar om mijlpalen te maken?</span>
                      {' '}De AI kan nu concrete mijlpalen uit dit gesprek halen.
                    </p>
                  </div>
                  <button
                    onClick={() => setOverlayState('SPARRING_COMPLETE')}
                    className="shrink-0 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <GoalIcon size={16} />
                    <span>Genereer Mijlpalen</span>
                  </button>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {/* Show prominent button for structured mode OR when few messages in free/reflection */}
                {(session?.mode === 'structured' || (session?.messages?.length || 0) < 4) && (
                  <button
                    onClick={() => session?.mode === 'structured' ? setShowMilestoneCapture(true) : setOverlayState('SPARRING_COMPLETE')}
                    disabled={!canAddMilestones}
                    className={milestoneInlineButtonClass}
                  >
                    <GoalIcon size={14} />
                    <span>{session?.mode === 'structured' ? milestoneButtonLabel : 'Genereer Mijlpalen'}</span>
                  </button>
                )}
                
                {/* Spraak Sparring Button */}
                {speechRecognitionService.isSupported() && (
                  <button
                    onClick={toggleVoiceMode}
                    disabled={isSending || isAIThinking}
                    className={`
                      flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-all
                      ${isVoiceMode 
                        ? 'bg-red-500 text-white animate-pulse' 
                        : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                      }
                      ${(isSending || isAIThinking) ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" 
                      />
                    </svg>
                    <span>{isVoiceMode ? 'Stop spraak' : 'Spraak sparring'}</span>
                  </button>
                )}
              </div>
              
              <button
                onClick={() => {
                  console.log('🎯 State Machine: Manual close requested, going to CLOSE_DIALOG');
                  setOverlayState('CLOSE_DIALOG');
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span>Sessie afsluiten</span>
              </button>
            </div>
          </>
        )}
        
        {/* Voice mode indicator with mute button */}
        {isVoiceMode && !isSessionComplete && (
          <div className={`mb-3 p-3 border rounded-lg ${isMicMuted ? 'bg-gray-50 border-gray-300' : 'bg-blue-50 border-blue-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${
                  isMicMuted ? 'bg-gray-400' : 
                  isListening ? 'bg-red-500 animate-pulse' : 'bg-blue-500'
                }`}></div>
                <span className={`text-sm font-medium ${isMicMuted ? 'text-gray-600' : 'text-blue-700'}`}>
                  {isMicMuted ? 'Mic uit - je kunt nu praten zonder transcriptie' : 
                   isListening ? 'Luisteren... Spreek nu' : 'Spraak modus actief'}
                </span>
              </div>
              
              {/* Mic mute toggle button */}
              <button
                onClick={toggleMicMute}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  isMicMuted 
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
                title={isMicMuted ? 'Klik om mic weer aan te zetten' : 'Klik om mic tijdelijk uit te zetten'}
              >
                {isMicMuted ? (
                  <>
                    {/* Mic off icon */}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" 
                      />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" 
                      />
                    </svg>
                    <span>Mic aan</span>
                  </>
                ) : (
                  <>
                    {/* Mic mute icon */}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" 
                      />
                    </svg>
                    <span>Mic uit</span>
                  </>
                )}
              </button>
            </div>
            {interimTranscript && !isMicMuted && (
              <p className="mt-2 text-sm text-blue-600 italic">"{interimTranscript}"</p>
            )}
          </div>
        )}
        
        {/* Input area - hidden when session complete */}
        {!isSessionComplete && (
          <>
            <div className="flex gap-2">
              {/* Text input */}
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={inputValue + (interimTranscript ? (inputValue ? ' ' : '') + interimTranscript : '')}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={isVoiceMode ? (isMicMuted ? 'Mic gedempt - typ of unmute om te spreken' : 'Spraak modus actief - of typ hier...') : 'Typ je bericht...'}
                  disabled={isSending || isAIThinking}
                  rows={2}
                  className={`
                    w-full px-3 py-2 border rounded-lg resize-none 
                    focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent 
                    disabled:bg-gray-50
                    ${isVoiceMode ? 'border-blue-300 bg-blue-50/50' : 'border-gray-300'}
                  `}
                />
              </div>

              {/* Send button */}
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || isSending || isAIThinking}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors self-end flex-shrink-0"
              >
                {isSending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  'Verstuur'
                )}
              </button>
            </div>
            
            <p className="text-xs text-gray-400 mt-2">
              Enter om te versturen, Shift+Enter voor nieuwe regel
            </p>
          </>
        )}
        
        {/* Session complete message - only show if no overlay active */}
        {isSessionComplete && !showMilestoneCapture && overlayState === 'NONE' && (
          <p className="text-sm text-gray-600 text-center font-medium">
            <span className="inline-flex items-center gap-1"><SuccessIcon size={16} /> Sparring voltooid! Druk op "Genereer Mijlpalen" om je mijlpalen automatisch aan te maken.</span>
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * MessageBubble - Visual representation of a message with optional edit functionality
 */
interface MessageBubbleProps {
  message: SparringMessage;
  onEdit?: (messageId: string, newContent: string) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isUser = message.role === 'user';

  // Focus textarea when editing starts
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(editContent.length, editContent.length);
    }
  }, [isEditing]);

  const handleSaveEdit = () => {
    if (editContent.trim() && editContent !== message.content && onEdit) {
      onEdit(message.id, editContent.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditContent(message.content);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    }
    if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-4 py-2 ${
          isUser
            ? 'bg-orange-50 text-gray-800 border border-orange-100'
            : 'bg-gray-100 text-gray-900'
        }`}
      >
        {isEditing ? (
          // Edit mode - larger textarea, subtle styling
          <div className="space-y-2">
            <textarea
              ref={textareaRef}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={5}
              className="w-full min-w-[280px] px-3 py-2 text-gray-800 rounded-lg border border-orange-100 bg-white focus:outline-none focus:border-orange-200 resize-y text-sm"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1.5 text-xs text-orange-500 hover:text-orange-600 transition-colors"
              >
                Opslaan
              </button>
            </div>
          </div>
        ) : (
          // View mode - subtle edit button
          <>
            <p className="whitespace-pre-wrap break-words">{message.content}</p>
            <div className={`flex items-center justify-between mt-1 text-xs ${
              isUser ? 'text-orange-300' : 'text-gray-400'
            }`}>
              <span>
                {new Date(message.timestamp).toLocaleTimeString('nl-NL', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {message.editedAt && ' (bewerkt)'}
              </span>
              {onEdit && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="ml-2 p-1 rounded opacity-40 hover:opacity-100 transition-opacity"
                  title="Bewerk antwoord"
                >
                  <EditIcon size={14} />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SparringChat;
